/**
 * Separate Axios Clients for Django and Spring
 * Each client handles authentication, error handling, and logging
 */

import axios from "axios";
import { getDjangoBaseURL, getSpringBaseURL } from "./apiConfig";
import forceLogout from "../Utils/forceLogout";

/**
 * Create axios instance
 * @param {String} baseURL - Base URL for the API
 * @param {String} serviceName - Name of the service for logging
 */
const createAxiosInstance = (baseURL, serviceName = "API") => {
  const instance = axios.create({
    baseURL,
    timeout: 30000, // 30 seconds
    headers: {
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
    },
  });

  // Request Interceptor
  instance.interceptors.request.use(
    (config) => {
      // Only add token if request is protected (default is true)
      const isProtected = config._isProtected !== false;

      if (isProtected) {
        // Get token from localStorage
        let token = localStorage.getItem("token");

        if (token) {
          // Handle master token format
          if (token.includes("||master")) {
            token = token.replace(/(\|\|master)+$/, "");
          }
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      // Clean up custom property (not needed in actual request)
      delete config._isProtected;

      return config;
    },
    (error) => {
      console.error(`[${serviceName}] Request Interceptor Error:`, error);
      return Promise.reject(error);
    }
  );

  // Response Interceptor
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      // Enhanced error handling
      const errorResponse = {
        message: "An error occurred",
        status: null,
        data: null,
      };

      if (error.response) {
        // Server responded with error status
        errorResponse.status = error.response.status;
        errorResponse.data = error.response.data;
        errorResponse.message =
          error.response.data?.error ||
          error.response.data?.message ||
          error.response.data?.detail ||
          `Request failed with status ${error.response.status}`;

        // Handle specific error codes
        switch (error.response.status) {
          case 401:
            // Unauthorized - token expired or invalid
            console.error(
              `[${serviceName}] Unauthorized access - token may be expired`
            );
            forceLogout(
              error.response.data?.code ||
                error.response.headers?.["x-auth-action"] ||
                "SESSION_REQUIRES_RELOGIN"
            );
            break;
          case 403:
            // Forbidden
            console.error(`[${serviceName}] Access forbidden`);
            break;
          case 404:
            console.error(`[${serviceName}] Resource not found`);
            break;
          case 500:
            console.error(`[${serviceName}] Internal server error`);
            break;
          default:
            console.error(
              `[${serviceName}] Error ${error.response.status}:`,
              errorResponse.message
            );
        }
      } else if (error.request) {
        // Request made but no response received
        errorResponse.message =
          "No response from server. Please check your connection.";
        console.error(`[${serviceName}] No response received:`, error.request);
      } else {
        // Something happened in setting up the request
        errorResponse.message = error.message || "Request setup failed";
        console.error(`[${serviceName}] Request setup error:`, error.message);
      }

      return Promise.reject(errorResponse);
    }
  );

  return instance;
};

// Create Django API client
export const djangoClient = createAxiosInstance(
  getDjangoBaseURL(),
  "Django API"
);

// Create Spring API client
export const springClient = createAxiosInstance(
  getSpringBaseURL(),
  "Spring API"
);

// Export default (Spring for backward compatibility)
export default springClient;
